import { useTranslation } from "react-i18next";
import { useShuffled } from "~/lib/use-shuffled";
import {
  ReviewsCarousel,
  reviewCardsFrom,
  withStockPhotos,
  type StockPhoto,
} from "~/components/reviews/ReviewsCarousel";
import {
  storeRatingItems,
  type StoreRating,
} from "~/components/reviews/StoreRatings";
import type { ProductReview } from "~/lib/reviews";
import { imageUrl } from "~/lib/image-url";
// Logical paths into media/ (not JS imports): see the comment on bgImageSet in
// app/components/home/UseCases.tsx. HighlightCard renders them through <Picture>, so browsers
// fetch the smaller AVIF/WebP; the PNGs are ~2 MB, full-frame Figma exports — `position` recenters
// each one's focal point since HighlightCard shows them at a fixed, non-square size.
const STOCK_IMAGES: StockPhoto[] = [
  { src: imageUrl("/images/review-images/couple-on-phone.png"), position: "40% 50%" },
  { src: imageUrl("/images/review-images/woman-on-phone.png"), position: "1% 50%" },
  { src: imageUrl("/images/review-images/man-on-tablet.png"), position: "87% 50%" },
  { src: imageUrl("/images/review-images/couple-on-porch.png"), position: "33% 50%" },
  { src: imageUrl("/images/review-images/man-on-tablet-2.png"), position: "24% 50%" },
  { src: imageUrl("/images/review-images/man-on-phone.png"), position: "19% 50%" },
];

export interface ReviewsProps {
  reviews: ProductReview[];
  storeRatings: StoreRating[];
}

export default function Reviews({ reviews, storeRatings }: ReviewsProps) {
  const { t, i18n } = useTranslation("home");

  const stockImages = useShuffled(STOCK_IMAGES);
  const cards = withStockPhotos(
    reviewCardsFrom(reviews, {
      anonymousReviewer: t("reviews.anonymous_reviewer"),
      language: i18n.language,
    }),
    stockImages,
  );

  return (
    <ReviewsCarousel
      headline={t("reviews.headline")}
      ariaLabel="Customer reviews"
      cards={cards}
      socialProof={storeRatingItems(storeRatings)}
    />
  );
}
